import { useEffect, useState } from "react";

// Consent Mode v2 — "basic" mode: gtag.js is only loaded after explicit consent.
const STORAGE_KEY = "rp:cookie-consent-v1";
const OPEN_EVENT = "rp:open-cookie-settings";
const GA_ID = process.env.REACT_APP_GA_ID;
const ALLOWED_HOSTS = ["rougepremiere.com", "www.rougepremiere.com"];

export const isAnalyticsHost = () =>
  ALLOWED_HOSTS.includes(window.location.hostname);

export const openCookieSettings = () =>
  window.dispatchEvent(new Event(OPEN_EVENT));

const gtag = function () {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
};

const loadGa = () => {
  if (!GA_ID || !isAnalyticsHost() || window.__rpGaLoaded) return;
  window.__rpGaLoaded = true;
  window[`ga-disable-${GA_ID}`] = false;
  window.gtag = gtag;
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    wait_for_update: 500,
  });
  gtag("consent", "update", { analytics_storage: "granted" });
  gtag("js", new Date());
  gtag("config", GA_ID, { anonymize_ip: true });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
};

const revokeGa = () => {
  if (!GA_ID) return;
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", { analytics_storage: "denied" });
  }
  window[`ga-disable-${GA_ID}`] = true;
  const domains = ["", window.location.hostname, `.${window.location.hostname.replace(/^www\./, "")}`];
  document.cookie.split(";").forEach((c) => {
    const name = c.split("=")[0].trim();
    if (!name.startsWith("_ga")) return;
    domains.forEach((d) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ""}`;
    });
  });
};

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) setVisible(true);
      else if (JSON.parse(raw).analytics) loadGa();
    } catch {
      setVisible(true);
    }
    const open = () => setVisible(true);
    window.addEventListener(OPEN_EVENT, open);
    return () => window.removeEventListener(OPEN_EVENT, open);
  }, []);

  const persist = (analytics) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ analytics, at: new Date().toISOString() }),
      );
    } catch {}
    if (analytics) loadGa();
    else revokeGa();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Bandeau de consentement cookies"
      data-testid="cookie-consent"
      className="fixed inset-x-3 bottom-3 md:inset-x-auto md:right-6 md:bottom-6 md:max-w-md z-[70] bg-white border border-[var(--line-strong)] rounded-md shadow-[0_20px_60px_-30px_rgba(0,0,0,0.35)] overflow-hidden"
    >
      <div className="h-[3px] w-full bg-[var(--red)]" aria-hidden />
      <div className="p-5">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--red)] font-medium">
          Cookies
        </div>
        <p className="mt-2 text-sm text-neutral-700 leading-relaxed">
          Rouge Première utilise des cookies de mesure d'audience pour comprendre
          la fréquentation du site. Aucune donnée n'est partagée à des fins
          publicitaires.{" "}
          <a
            href="/mentions-legales"
            className="text-[var(--red)] underline underline-offset-2"
          >
            En savoir plus
          </a>
          .
        </p>
        <div className="mt-4 flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            data-testid="cookie-refuse"
            onClick={() => persist(false)}
            className="text-xs uppercase tracking-[0.25em] px-3 py-2 text-neutral-500 hover:text-black transition-colors"
          >
            Refuser
          </button>
          <button
            type="button"
            data-testid="cookie-accept"
            onClick={() => persist(true)}
            className="text-xs uppercase tracking-[0.25em] px-4 py-2 bg-black text-white hover:bg-[var(--red)] transition-colors rounded-sm"
          >
            Accepter
          </button>
        </div>
      </div>
    </div>
  );
};
